import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useAuth } from '../../contexts/AuthContext';
import { aiAPI } from '../../services/api';
import VerifyToUnlock from '../../components/ai/VerifyToUnlock';
import { CodeBlock } from '../../components/ai/SafeMarkdown';

interface FixStep { platform: string; steps: string[]; config_snippet: string }
interface Finding {
  id: string; title: string; severity: string; category: string; evidence: string;
  business_impact: string; effort: string; fix_steps: FixStep[];
}
interface Report {
  risk_score: number; risk_level: string; executive_summary: string;
  findings: Finding[]; quick_wins: string[]; already_good: string[];
}
interface Diff {
  previous_report_id: number | null; score_change: number | null; summary: string;
  fixed: { id: string; title: string; severity: string }[]; new: { id: string; title: string; severity: string }[];
}
interface AuditRow {
  id: number; status: string; trigger_type: string; risk_score: number | null; grade: string | null;
  diff: Diff | null; error_message: string | null; created_at: string; completed_at: string | null;
  findings_count: number; report?: Report;
}

const SEV_BADGE: Record<string, string> = {
  critical: 'bg-danger', high: 'bg-warning text-dark', medium: 'bg-info text-dark', low: 'bg-secondary', info: 'bg-light text-dark',
};
const GRADE_COLOR: Record<string, string> = { 'A+': '#16a34a', A: '#16a34a', B: '#65a30d', C: '#ca8a04', D: '#ea580c', F: '#dc2626' };
const PLATFORM_LABEL: Record<string, string> = {
  generic: 'General', nginx: 'Nginx', apache: 'Apache', iis: 'IIS', wordpress: 'WordPress', cloudflare: 'Cloudflare',
  dns: 'DNS', hosting_panel: 'Hosting panel', code: 'Code',
};

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString() : '—');

const FindingCard: React.FC<{ f: Finding; index: number }> = ({ f, index }) => {
  const [open, setOpen] = useState(index < 2);
  const [platform, setPlatform] = useState(0);
  const step = f.fix_steps[platform];
  return (
    <div className="card mb-2 border-0 shadow-sm">
      <div className="card-body py-2" role="button" onClick={() => setOpen(!open)}>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small" style={{ width: 22 }}>{index + 1}.</span>
          <span className={`badge ${SEV_BADGE[f.severity] || 'bg-secondary'} text-uppercase`}>{f.severity}</span>
          <span className="fw-semibold flex-grow-1">{f.title}</span>
          <span className="badge bg-light text-dark d-none d-md-inline"><i className="bi bi-clock me-1" />{f.effort}</span>
          <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
        </div>
      </div>
      {open && (
        <div className="card-body pt-0">
          <div className="row g-3 small">
            <div className="col-md-6"><div className="text-muted fw-semibold mb-1">Why it matters</div>{f.business_impact}</div>
            <div className="col-md-6"><div className="text-muted fw-semibold mb-1">Evidence</div>{f.evidence}</div>
          </div>
          {f.fix_steps.length > 0 && (
            <div className="mt-3">
              <div className="text-muted fw-semibold small mb-1">How to fix</div>
              <ul className="nav nav-pills nav-sm mb-2 small">
                {f.fix_steps.map((s, i) => (
                  <li className="nav-item" key={i}>
                    <button type="button" className={`nav-link py-1 px-2 ${i === platform ? 'active' : ''}`} onClick={() => setPlatform(i)}>
                      {PLATFORM_LABEL[s.platform] || s.platform}
                    </button>
                  </li>
                ))}
              </ul>
              {step && (
                <>
                  <ol className="small ps-3 mb-2">{step.steps.map((t, i) => <li key={i}>{t}</li>)}</ol>
                  {step.config_snippet && <CodeBlock code={step.config_snippet} lang={step.platform} />}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AiSecurityAuditPage: React.FC = () => {
  useDocumentTitle('AI Security Audit');
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [verified, setVerified] = useState<boolean>(!!currentAsset?.is_verified);
  const [history, setHistory] = useState<AuditRow[]>([]);
  const [selected, setSelected] = useState<AuditRow | null>(null);
  const [running, setRunning] = useState<AuditRow | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadReport = useCallback(async (id: number) => {
    if (!assetId) return;
    const r = await aiAPI.getAudit(assetId, id);
    setSelected(r.data.data);
  }, [assetId]);

  const load = useCallback(async () => {
    if (!assetId) return;
    try {
      const [st, list] = await Promise.all([aiAPI.status(assetId), aiAPI.listAudits(assetId)]);
      setAvailable(!!st.data.data.available);
      setVerified(!!st.data.data.verified);
      const rows: AuditRow[] = list.data.data || [];
      setHistory(rows);
      const active = rows.find((r) => r.status === 'queued' || r.status === 'running') || null;
      setRunning(active);
      const latest = rows.find((r) => r.status === 'completed');
      if (latest && (!selected || selected.id === latest.id || !rows.some((r) => r.id === selected.id))) await loadReport(latest.id);
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error(e?.response?.data?.message || 'Failed to load AI audits');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, loadReport]);

  useEffect(() => { setSelected(null); setLoading(true); load(); }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while a job is running
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!running || !assetId) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await aiAPI.getAudit(assetId, running.id);
        const row: AuditRow = r.data.data;
        if (row.status === 'completed') {
          toast.success('AI security audit ready');
          setRunning(null);
          setSelected(row);
          load();
        } else if (row.status === 'failed') {
          toast.error(row.error_message || 'Audit failed');
          setRunning(null);
          load();
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [running, assetId, load]);

  const generate = async () => {
    if (!assetId) return;
    setStarting(true);
    try {
      const r = await aiAPI.generateAudit(assetId);
      setRunning({ id: r.data.data.id, status: r.data.data.status } as AuditRow);
      toast.success(r.data.data.existing ? 'An audit is already running' : 'AI audit started — this takes about a minute');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not start the audit');
    } finally {
      setStarting(false);
    }
  };

  const report = selected?.report;
  const diff = selected?.diff;

  return (
    <DashboardLayout>
      <div className="container-fluid py-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div>
            <h4 className="mb-0"><i className="bi bi-stars me-2 text-primary" />AI Security Audit</h4>
            <div className="text-muted small">Claude reviews everything CloudAyn knows about your site and tells you what to fix first.</div>
          </div>
          {verified && available && (
            <div className="d-flex gap-2">
              <Link to="/security/ai-assistant" className="btn btn-outline-primary"><i className="bi bi-chat-dots me-1" />Ask the assistant</Link>
              <button className="btn btn-primary" onClick={generate} disabled={starting || !!running}>
                {running || starting
                  ? (<><span className="spinner-border spinner-border-sm me-2" />Auditing…</>)
                  : (<><i className="bi bi-play-fill me-1" />Run AI audit</>)}
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
        ) : !verified ? (
          <VerifyToUnlock feature="AI security audits" />
        ) : !available ? (
          <div className="alert alert-warning">AI features are temporarily unavailable. Please try again later.</div>
        ) : (
          <>
            {running && (
              <div className="alert alert-info d-flex align-items-center">
                <span className="spinner-border spinner-border-sm me-3" />
                Gathering scan results, uptime, SSL, email DNS and malware data and asking Claude for a prioritised plan…
              </div>
            )}

            {!report && !running && (
              <div className="card border-0 shadow-sm"><div className="card-body text-center py-5">
                <i className="bi bi-shield-check text-primary" style={{ fontSize: '2.5rem' }} />
                <h5 className="mt-3">No AI audit yet</h5>
                <p className="text-muted">Run your first audit to get a plain-language risk grade and exact fix steps.
                  For best results, run a <Link to="/security/scanning">security scan</Link> first.</p>
              </div></div>
            )}

            {report && selected && (
              <div className="row g-3">
                <div className="col-lg-8">
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body d-flex gap-4 align-items-center flex-wrap">
                      <div className="text-center" style={{ minWidth: 90 }}>
                        <div style={{ fontSize: '3rem', fontWeight: 700, lineHeight: 1, color: GRADE_COLOR[selected.grade || ''] || '#6b7280' }}>{selected.grade}</div>
                        <div className="small text-muted mt-1">Risk {report.risk_score}/100</div>
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: 240 }}>
                        <div className="small text-muted mb-1">
                          {selected.trigger_type === 'scheduled' ? 'Weekly audit' : 'Audit'} · {fmt(selected.completed_at)} · risk level <strong>{report.risk_level}</strong>
                        </div>
                        <p className="mb-2">{report.executive_summary}</p>
                        {diff && diff.previous_report_id && (
                          <div className={`small rounded px-2 py-1 d-inline-block ${diff.fixed.length ? 'bg-success-subtle text-success-emphasis' : 'bg-light'}`}>
                            <i className="bi bi-arrow-left-right me-1" />{diff.summary}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <h6 className="fw-bold">Prioritised findings ({report.findings.length})</h6>
                  {report.findings.length === 0 && <div className="text-muted small mb-3">No issues found in the available data.</div>}
                  {report.findings.map((f, i) => <FindingCard key={`${selected.id}-${f.id}`} f={f} index={i} />)}
                </div>

                <div className="col-lg-4">
                  {report.quick_wins.length > 0 && (
                    <div className="card border-0 shadow-sm mb-3"><div className="card-body">
                      <h6 className="fw-bold"><i className="bi bi-lightning-charge text-warning me-1" />Quick wins</h6>
                      <ul className="small ps-3 mb-0">{report.quick_wins.map((q, i) => <li key={i} className="mb-1">{q}</li>)}</ul>
                    </div></div>
                  )}
                  {diff && diff.fixed.length > 0 && (
                    <div className="card border-0 shadow-sm mb-3"><div className="card-body">
                      <h6 className="fw-bold"><i className="bi bi-check-circle text-success me-1" />Fixed since last audit</h6>
                      <ul className="small ps-3 mb-0">{diff.fixed.map((f) => <li key={f.id} className="text-decoration-line-through text-muted">{f.title}</li>)}</ul>
                    </div></div>
                  )}
                  {report.already_good.length > 0 && (
                    <div className="card border-0 shadow-sm mb-3"><div className="card-body">
                      <h6 className="fw-bold"><i className="bi bi-hand-thumbs-up text-success me-1" />Already good</h6>
                      <ul className="small ps-3 mb-0">{report.already_good.map((q, i) => <li key={i} className="mb-1">{q}</li>)}</ul>
                    </div></div>
                  )}
                  <div className="card border-0 shadow-sm"><div className="card-body">
                    <h6 className="fw-bold"><i className="bi bi-clock-history me-1" />History</h6>
                    <div className="list-group list-group-flush small">
                      {history.map((h) => (
                        <button key={h.id} type="button"
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center px-1 ${selected.id === h.id ? 'active' : ''}`}
                          disabled={h.status !== 'completed'} onClick={() => loadReport(h.id)}>
                          <span>{fmt(h.created_at)}{h.trigger_type === 'scheduled' ? ' · weekly' : ''}</span>
                          <span>{h.status === 'completed' ? <strong>{h.grade}</strong> : <span className="text-muted">{h.status}</span>}</span>
                        </button>
                      ))}
                    </div>
                    <div className="text-muted small mt-2">
                      Weekly audits run automatically when weekly security emails are on in <Link to="/settings/notifications">notification settings</Link>.
                    </div>
                  </div></div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AiSecurityAuditPage;
